import { Avatar, Chip, Tooltip, styled } from "@mui/material";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";

type Props = {
  label: string;
  productTag: string;
  iconUrl?: string;
  hasInfo: boolean;
  onSelect: (productTag: string) => void;
};

type StyledProductChipProps = {
  hasInfo: boolean;
  hasIcon: boolean;
};

type StyledProductAvatarProps = {
  hasInfo: boolean;
};

const StyledProductChip = styled(Chip, {
  shouldForwardProp: (prop) => prop !== "hasInfo" && prop !== "hasIcon",
})<StyledProductChipProps>(({ hasInfo, hasIcon, theme }) => ({
  display: "inline-flex",
  alignItems: "center",
  gap: theme.spacing(0.75),
  borderRadius: 0,
  transition: "all 0.2s cubic-bezier(0.4, 0, 0.2, 1)",
  height: 36,
  paddingLeft: hasIcon ? theme.spacing(0.75) : theme.spacing(1.5),
  paddingRight: theme.spacing(0.75),
  borderWidth: "1.2px",
  "& .MuiChip-avatar": {
    marginLeft: 0,
    marginRight: 0,
  },
  ...(hasInfo
    ? {
        backgroundColor: "#f1f8e9",
        borderColor: "#2e7d32",
        boxShadow: "0 2px 5px rgba(0,0,0,0.1)",
        cursor: "pointer",
        "& .MuiChip-label": {
          color: "#1b5e20",
          fontSize: "0.85rem",
          fontWeight: 600,
          paddingLeft: 0,
          paddingRight: 0,
        },
        "& .MuiChip-deleteIcon": {
          color: "#2e7d32",
          marginLeft: 0,
          marginRight: 0,
          "&:hover": {
            color: "#1b5e20",
          },
        },
        "&:hover": {
          backgroundColor: "#e8f5e9",
          borderColor: "#1b5e20",
          boxShadow: "0 4px 10px rgba(0,0,0,0.15)",
          transform: "translateY(-1.5px)",
        },
        "&:active": {
          transform: "translateY(0)",
          boxShadow: "0 1px 2px rgba(0,0,0,0.1)",
        },
      }
    : {
        borderColor: theme.palette.divider,
        borderStyle: "dashed",
        backgroundColor: "rgba(0,0,0,0.02)",
        cursor: "default",
        "& .MuiChip-label": {
          color: theme.palette.text.disabled,
          fontSize: "0.8rem",
          paddingLeft: 0,
          paddingRight: 0,
        },
      }),
}));

const StyledProductAvatar = styled(Avatar, {
  shouldForwardProp: (prop) => prop !== "hasInfo",
})<StyledProductAvatarProps>(({ hasInfo }) => ({
  width: 20,
  height: 20,
  backgroundColor: "transparent",
  borderRadius: 0,
  opacity: hasInfo ? 1 : 0.45,
}));

const MapMarkerRainforestProductChip = ({
  label,
  productTag,
  iconUrl,
  hasInfo,
  onSelect,
}: Props) => {
  const handleSelect = () => {
    if (hasInfo) {
      onSelect(productTag);
    }
  };

  return (
    <Tooltip
      title={hasInfo ? "Click to view details" : "More information coming soon"}
      arrow
    >
      <StyledProductChip
        label={label}
        size="small"
        variant="outlined"
        clickable={hasInfo}
        hasInfo={hasInfo}
        hasIcon={!!iconUrl}
        onClick={hasInfo ? handleSelect : undefined}
        deleteIcon={
          hasInfo ? (
            <ChevronRightIcon sx={{ fontSize: "16px !important" }} />
          ) : undefined
        }
        onDelete={hasInfo ? handleSelect : undefined}
        avatar={
          iconUrl ? (
            <StyledProductAvatar
              src={iconUrl}
              alt={productTag}
              hasInfo={hasInfo}
            />
          ) : undefined
        }
      />
    </Tooltip>
  );
};

export default MapMarkerRainforestProductChip;
